import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { SettingIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getInsurance } from '../../../api/insurances/get-insurance'
import { updateInsurance } from '../../../api/insurances/update-insurance'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import { InsuranceForm } from '../../../components/pages/insurance/insurance-form'
import { Paths } from '../../../lib/constants/path'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../main'
import NotFoundPage from '../../common/not-found'

export function InsuranceUpdatePage() {
  const { navigate, back } = useNavigator()
  const { id } = useParams<{ id: string }>()
  const queryKey = ['insurances', id]

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant d'assurance invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateInsurance,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey })
      toast.success('Assurance modifiée avec succès')
    }
  })

  const { data, isPending } = useQuery({
    queryKey,
    queryFn: () => getInsurance(+id)
  })

  if (isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier une assurance'} icon={SettingIcon} />
      <div className={'h-4'} />

      <div className="space-y-4">
        <Box>
          <InsuranceForm
            defaultValues={{
              ...data,
              elligibleDestinationCountries: data.elligibleDestinationCountries.map((c) => c.id)
            }}
            buttonsConfig={updateButtonsConfig({
              onUpdate: async (values) => {
                await mutateAsync({ id: data.id, ...values })
              },
              onUpdateThenCreate: async (values) => {
                await mutateAsync({ id: data.id, ...values })
                navigate(Paths.INSURANCE_CREATE_PAGE)
              },
              onUpdateThenList: async (values) => {
                await mutateAsync({ id: data.id, ...values })
                navigate(Paths.INSURANCE_LIST_PAGE, { replace: true })
              },
              onCancel: () => back(Paths.INSURANCE_LIST_PAGE)
            })}
          />
        </Box>
      </div>
    </div>
  )
}
